import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@akuna/ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { useTranslation } from "react-i18next";

export default function WhatsAppMetaCallback() {
  const { t } = useTranslation("settings");
  const [params] = useSearchParams();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  const code = params.get("code");
  const wabaId = params.get("wabaId") || params.get("waba_id");
  const metaBusinessId = params.get("metaBusinessId") || params.get("business_id") || params.get("meta_business_id");
  const phoneNumberId = params.get("phoneNumberId") || params.get("phone_number_id");
  const event = params.get("event") || params.get("type");
  const isFinishEvent = String(event || "").toUpperCase() === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING";

  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  const missing = useMemo(() => {
    const parts: string[] = [];
    if (!clinicId) parts.push(t("whatsappMetaCallbackMissingClinicId"));
    if (!code) parts.push("code");
    if (event && !isFinishEvent) parts.push("FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING event");
    return parts;
  }, [clinicId, code, event, isFinishEvent, t]);

  const connect = useCallback(async () => {
    if (!clinicId || !code) return;
    setIsConnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke("meta-embedded-signup-callback", {
        body: {
          clinicId,
          code,
          ...(event ? { embeddedSignupPayload: { event, type: event } } : {}),
          wabaId,
          metaBusinessId,
          phoneNumberId,
        },
      });

      const result = data as {
        success?: boolean;
        connected?: boolean;
        expiresAt?: string | null;
        finishSync?: {
          smbAppStateSync?: boolean;
          history?: boolean;
          subscribedApps?: boolean;
          smbAppStateSyncError?: string | null;
          historyError?: string | null;
          subscribedAppsError?: string | null;
        };
        error?: string;
      } | null;
      if (error || !result?.success) {
        toast.error(error?.message || result?.error || t("whatsappMetaCallbackConnectError"));
        return;
      }
      if (result.connected !== true) {
        toast.error(t("whatsappMetaCallbackConnectError"));
        return;
      }
      setIsConnected(true);
      setExpiresAt(result.expiresAt ?? null);
      toast.success(t("whatsappMetaCallbackConnectSuccess"));
    } finally {
      setIsConnecting(false);
    }
  }, [clinicId, code, event, metaBusinessId, phoneNumberId, wabaId, t]);

  useEffect(() => {
    if (missing.length === 0 && !isConnecting && !isConnected) {
      void connect();
    }
  }, [missing.length, isConnected, isConnecting, connect]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("whatsappMetaCallbackTitle")}</CardTitle>
          <CardDescription>{t("whatsappMetaCallbackSubtitle")}</CardDescription>
        </CardHeader>
        <CardContent className={`relative space-y-4 ${isConnecting ? "pointer-events-none opacity-75" : ""}`}>
          {missing.length > 0 ? (
            <div className="text-sm text-destructive">{t("whatsappMetaCallbackMissingParams", { params: missing.join(", ") })}</div>
          ) : null}

          <div className={`relative flex gap-2 ${isConnecting ? "pointer-events-none opacity-75" : ""}`}>
            <Button onClick={connect} disabled={isConnecting || missing.length > 0}>
              {isConnecting ? t("whatsappMetaCallbackConnecting") : t("whatsappMetaCallbackFinishConnect")}
            </Button>
            <Button asChild variant="outline" className={isConnecting ? "pointer-events-none" : undefined}>
              <Link to="/settings/whatsapp-meta">{t("whatsappMetaCallbackBackToSettings")}</Link>
            </Button>
            {isConnecting ? <Loader2 className="h-4 w-4 animate-spin absolute right-2 top-2 text-muted-foreground" /> : null}
          </div>

          {isConnected ? (
            <div className="rounded-md border p-3 space-y-2">
              <div className="text-sm font-medium">{t("whatsappMetaCallbackStoredTitle")}</div>
              {expiresAt ? <div className="text-xs text-muted-foreground">{t("whatsappMetaCallbackExpiresAt", { expiresAt })}</div> : null}
              <p className="text-xs text-muted-foreground">{t("whatsappMetaCallbackTokenHint")}</p>
            </div>
          ) : null}
          {isConnecting ? (
            <div className="absolute inset-0 flex items-center justify-center bg-background/70 rounded-md">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
